import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAppStore, setPoints } from '@/hooks/useAppStore';
import { refreshModelsConfig, useAppConfig } from '@/hooks/useAppConfig';
import { pushEvent } from '@/hooks/useAnalytics';
import {
  createGeneration,
  getGenerationSubmission,
  recordMembershipOfferEvent,
  uploadReference,
} from '@/api/generation';
import type {
  CreateGenerationRequest,
  GenerationResult,
  GenerationStatus,
  MembershipOffer,
  ReasonCode,
} from '@/api/generation';
import {
  getGenerationEntryConfig,
  getGenerationPricingQuote,
  modelSelectionMatchesGenerationEntry,
  modelSupportsGenerationEntry,
} from '@/api/config';
import { ApiError } from '@/api/client';
import { randomUUID } from '@/utils/uuid';
import { GENERATION_ACTION_LOGO_URL } from '@/config/brand';
import { track } from '@/utils/tracker';
import {
  clearLocalPendingByIdempotencyKey,
  clearLocalPending,
  findUnresolvedLocalPending,
  isLocalPendingReadyForReconcile,
  markLocalPendingForReconcile,
  readLocalPendings,
  restartLocalPendingReconcile,
  writeLocalPending,
} from './utils/pendingGeneration';
import { submissionReducer } from './utils/submissionState';
import {
  availableGenerationSlots,
  useGenerationTasks,
} from '@/contexts/generationTaskStore';
import {
  getGenerateEntryId,
  getGenerationOperation,
  getGenerationSelectionMode,
  resolveGenerationEntryContext,
  shouldResumeGenerationAfterPayment,
  type GenerateSourceEntry,
} from './utils/entryContext';

// Components from v1
import ConfigBar from './components/ConfigBar';
import StyleModal from './components/StyleModal';
import TemplateModal from './components/TemplateModal';
import PointsModal from './components/PointsModal';
import VipUpgradeSheet from './components/VipUpgradeSheet';
import ModelSelector from './components/ModelSelector';
import QualitySelector from './components/QualitySelector';
import RecentUsageModal from './components/RecentUsageModal';
import TaskStatusArea from './components/TaskStatusArea';
import ResultCard from './components/ResultCard';
import ShareSheet from './components/ShareSheet';
import GenerateButton from './components/GenerateButton';

// New components copied from v2.0
import ModeSelector from './components/ModeSelector';
import ChatView, { type ChatSubmissionRejection } from './components/ChatView';
import ModelGallery from './components/ModelGallery';
import RatioSelector from './components/RatioSelector';
import PromptAIHelpButton from './components/PromptAIHelpButton';
import { useEntryAIHelp } from './hooks/useEntryAIHelp';

const IMG2IMG_REFERENCE_LIMIT = 6;
const IMG2IMG_REFERENCE_ACCEPT = '.jpg,.jpeg,.png,image/jpeg,image/png';
const IMG2IMG_ALLOWED_REFERENCE_TYPES = new Set(['image/jpeg', 'image/png']);
const IMG2IMG_ALLOWED_REFERENCE_EXTENSIONS = /\.(jpe?g|png)$/i;
const SUBMISSION_RECONCILE_INTERVAL_MS = 2_000;
const SUBMISSION_RECONCILE_MAX_ATTEMPTS = 3;

type GenerateOptions = {
  forceMode?: string;
  forceRefUrl?: string;
  forceRefUrls?: string[];
  sourceEntry?: GenerateSourceEntry;
  resumeGenId?: string;
  retryIdempotencyKey?: string;
};

type UploadedReferenceImage = {
  id: string;
  previewUrl: string;
  remoteUrl?: string;
  uploading: boolean;
};

function isPollTimeout(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'GEN_TIMEOUT';
}

const modelIconMap: Record<string, string> = {
  doubao: 'ri-leaf-line',
  gpt: 'ri-robot-2-line',
  gpt_image_2: 'ri-robot-2-line',
  gemini: 'ri-image-ai-line',
  gemini_nano: 'ri-image-ai-line',
  nanobanana: 'ri-magic-line',
};

interface GenerateResultItem {
  imageUrl: string;
  thumbnailUrl?: string;
  styleName: string;
  ratio: string;
  prompt?: string;
  gen_id?: string;
  favorited?: boolean;
  canDownloadHd?: boolean;
}

type GenerateDraft = {
  prompt: string
  mode: string
  styleId: string | null
  ratio: string | null
  modelId: string
  quality: string
}

function readGenerateDraft(userId?: string): GenerateDraft | null {
  if (!userId) return null
  try {
    const value = JSON.parse(
      sessionStorage.getItem(`glowpic:generate:draft:${userId}`) ?? 'null',
    )
    return value && typeof value === 'object' ? value as GenerateDraft : null
  } catch {
    return null
  }
}

export default function GeneratePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const entryContext = useMemo(
    () => resolveGenerationEntryContext(searchParams),
    [searchParams],
  );
  const { user, authInitialized } = useAppStore();
  const restoredDraft = useMemo(() => readGenerateDraft(user?.userId), [user?.userId]);
  const {
    tasks,
    activeCount,
    activeLimit,
    capacityReady,
    acceptCreateAck,
    applyCapacity,
    refresh: refreshGenerationTasks,
  } = useGenerationTasks();
  const config = useAppConfig();
  const { t } = useTranslation();
  // ---- Mode state ----
  const [currentMode, setCurrentMode] = useState<string>(
    searchParams.has('mode') ? entryContext.mode : restoredDraft?.mode ?? entryContext.mode,
  );
  const currentModeRef = useRef(currentMode);
  const recoveringPendingRef = useRef(false);
  const [chatCostOverride, setChatCostOverride] = useState<number | null>(null);
  const [chatReferenceImageCount, setChatReferenceImageCount] = useState(0);
  const modeTasks = useMemo(
    () => tasks.filter((task) => task.tool_id === currentMode).slice(0, 2),
    [currentMode, tasks],
  );

  useEffect(() => {
    currentModeRef.current = currentMode;
  }, [currentMode]);

  useEffect(() => {
    pushEvent('generate_page_view', user?.userId, { mode: currentMode });
    track.pageView('generate');
    track.generatePageView({
      mode: currentMode,
      entry_id: getGenerateEntryId(currentMode),
      generation_operation: getGenerationOperation(currentMode),
    });
  }, [currentMode, user?.userId]);

  const handleModeChange = useCallback((mode: string) => {
    if (mode === currentMode) return;
    track.generateTabClick({
      mode,
      entry_id: getGenerateEntryId(mode),
      generation_operation: getGenerationOperation(mode),
    });
    if (referencePreviewUrlRef.current) {
      URL.revokeObjectURL(referencePreviewUrlRef.current);
      referencePreviewUrlRef.current = null;
    }
    primaryReferenceUploadSeqRef.current += 1;
    secondaryReferenceUploadSeqRef.current += 1;
    tryonModelUploadSeq.current += 1;
    setCurrentMode(mode);
    setChatCostOverride(null);
    setChatReferenceImageCount(0);
    setResult(null);
    setTaskStatus(null);
    setTaskReasonCode(null);
    setReferenceImage(null);
    setReferenceImageUrl(undefined);
    setImg2imgReferenceImages((images) => {
      images.forEach((image) => URL.revokeObjectURL(image.previewUrl));
      return [];
    });
    setSecondaryImage(null);
    setSecondaryImageUrl(undefined);
    setTryonModelUploading(false);
    setSelectedTryonModelId(null);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('mode', mode);
    setSearchParams(newParams, { replace: true });
  }, [currentMode, searchParams, setSearchParams]);

  // ---- Core state ----
  const [prompt, setPrompt] = useState(
    searchParams.has('prompt') ? entryContext.prompt : restoredDraft?.prompt ?? entryContext.prompt,
  );
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [referenceImageUrl, setReferenceImageUrl] = useState<string | undefined>(undefined);
  const referencePreviewUrlRef = useRef<string | null>(null);
  const [img2imgReferenceImages, setImg2imgReferenceImages] = useState<UploadedReferenceImage[]>([]);
  const img2imgReferenceImagesRef = useRef<UploadedReferenceImage[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(
    searchParams.has('styleId') ? entryContext.styleId : restoredDraft?.styleId ?? entryContext.styleId,
  );
  const [selectedRatio, setSelectedRatio] = useState<string | null>(
    searchParams.has('ratio') ? entryContext.ratio : restoredDraft?.ratio ?? entryContext.ratio,
  );
  const [selectedModel, setSelectedModel] = useState<string>(restoredDraft?.modelId ?? '');
  const modelInitialized = useRef(false);
  const modelTouched = useRef(Boolean(restoredDraft?.modelId));
  const [selectedQuality, setSelectedQuality] = useState<string>(
    restoredDraft?.quality ?? 'standard',
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeGenerationId, setActiveGenerationId] = useState<string | null>(null);
  const [result, setResult] = useState<GenerateResultItem | null>(null);
  const [taskStatus, setTaskStatus] = useState<GenerationStatus | null>(null);
  const [taskReasonCode, setTaskReasonCode] = useState<ReasonCode | null>(null);
  const [inlineMembershipOffer, setInlineMembershipOffer] = useState<{
    generationId: string;
    offer: MembershipOffer;
  } | null>(null);
  const [showInlineMembershipCta, setShowInlineMembershipCta] = useState(false);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [pointsModalCost, setPointsModalCost] = useState(0);
  const [showVipSheet, setShowVipSheet] = useState(false);
  const [shareSheet, setShareSheet] = useState<{ imageUrl: string; prompt: string; genId?: string } | null>(null);
  const [genToast, setGenToast] = useState<string | null>(null);
  const [chatSubmissionRejection, setChatSubmissionRejection] = useState<ChatSubmissionRejection | null>(null);
  const [, dispatchSubmission] = useReducer(submissionReducer, {});
  const genToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user?.userId) return
    const draft: GenerateDraft = {
      prompt,
      mode: currentMode,
      styleId: selectedStyle,
      ratio: selectedRatio,
      modelId: selectedModel,
      quality: selectedQuality,
    }
    sessionStorage.setItem(
      `glowpic:generate:draft:${user.userId}`,
      JSON.stringify(draft),
    )
  }, [
    currentMode,
    prompt,
    selectedModel,
    selectedQuality,
    selectedRatio,
    selectedStyle,
    user?.userId,
  ])

  // ---- Modal state ----
  const [showTemplateModal, setShowTemplateModal] = useState(() => !!entryContext.category);
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [showModelSheet, setShowModelSheet] = useState(false);
  const [showQualitySheet, setShowQualitySheet] = useState(false);
  const [showRatioSheet, setShowRatioSheet] = useState(false);
  const [showRecentModal, setShowRecentModal] = useState(false);
  const [templateCategory, setTemplateCategory] = useState<string | undefined>(
    entryContext.category || undefined,
  );
  const [templateCategoryTitleKey, setTemplateCategoryTitleKey] = useState<string | undefined>(() => {
    const cat = entryContext.category;
    return cat ? 'tpl_' + cat : undefined;
  });

  // ---- Mode-specific state ----
  const [secondaryImage, setSecondaryImage] = useState<string | null>(null);
  const [secondaryImageUrl, setSecondaryImageUrl] = useState<string | undefined>(undefined);
  const [editPrompt, setEditPrompt] = useState('');
  const [inlineAIHelpLoading, setInlineAIHelpLoading] = useState(false);
  const [inlineAIHelpError, setInlineAIHelpError] = useState<string | null>(null);
  const [restoreStrength, setRestoreStrength] = useState(80);
  const [selectedPose, setSelectedPose] = useState('natural');
  const [selectedTryonModelId, setSelectedTryonModelId] = useState<string | null>(null);
  const [tryonModelUploading, setTryonModelUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const secondaryFileInputRef = useRef<HTMLInputElement>(null);
  const primaryReferenceUploadSeqRef = useRef(0);
  const secondaryReferenceUploadSeqRef = useRef(0);
  const tryonModelUploadSeq = useRef(0);
  const inlineAIHelpRequestSeqRef = useRef(0);
  const activeGenerationRef = useRef<string | null>(null);
  const createInFlightRef = useRef<string | null>(null);

  // ---- Config helpers ----
  const appStyles = useMemo(() => config?.styles ?? [], [config?.styles]);
  const appRatios = useMemo(() => config?.ratios ?? [], [config?.ratios]);
  const appTemplates = useMemo(() => config?.templates ?? [], [config?.templates]);
  const appModels = useMemo(() => config?.models ?? [], [config?.models]);
  const selectionMode = getGenerationSelectionMode(currentMode, chatReferenceImageCount);
  const selectionReferenceImageCount = currentMode === 'text2img'
    ? chatReferenceImageCount
    : currentMode === 'img2img'
      ? img2imgReferenceImages.length
      : currentMode === 'tryon'
        ? 2
        : referenceImageUrl
          ? 1
          : 0;
  const entryConfig = useMemo(
    () => getGenerationEntryConfig(config, selectionMode),
    [config, selectionMode],
  );
  const {
    aiHelpProfile,
    aiHelpDisabled,
    expandEntryPrompt,
  } = useEntryAIHelp({
    profile: entryConfig?.ai_help_profile,
    mode: selectionMode,
    toolId: selectionMode,
    user,
  });
  const tryonModels = useMemo(() => config?.tryon_models ?? [], [config?.tryon_models]);
  const availableModels = useMemo(
    () => appModels.filter((model) => (
      modelSupportsGenerationEntry(model, entryConfig, selectionReferenceImageCount)
    )),
    [appModels, entryConfig, selectionReferenceImageCount],
  );
  const selectedModelConfig = useMemo(
    () => availableModels.find((model) => model.id === selectedModel),
    [availableModels, selectedModel],
  );

  useEffect(() => {
    inlineAIHelpRequestSeqRef.current += 1;
    setInlineAIHelpLoading(false);
    setInlineAIHelpError(null);
  }, [aiHelpProfile, currentMode]);

  const getSelectedStyleName = useCallback(() => {
    return appStyles.find((s) => s.id === selectedStyle)?.name || '';
  }, [selectedStyle, appStyles]);

  const getSelectedRatioLabel = useCallback(() => {
    return appRatios.find((r) => r.id === selectedRatio)?.label || '';
  }, [selectedRatio, appRatios]);

  const getSelectedTemplateName = useCallback(() => {
    const tpl = appTemplates.find((tItem) => tItem.id === selectedTemplate);
    if (!tpl) return '';
    return tpl.name;
  }, [selectedTemplate, appTemplates]);

  // ---- Model initialization from config ----
  useEffect(() => {
    if (!availableModels.length) {
      setSelectedModel('');
      setSelectedQuality('');
      // Preserve an explicit deep-link ratio while the asynchronous config is
      // still loading. Once models arrive, the capability effect below
      // validates it and falls back only when the model does not support it.
      return;
    }
    const requestedModel = entryContext.modelId;
    const requested = requestedModel
      ? availableModels.find((m) => m.id === requestedModel)
      : undefined;
    const configuredDefault = availableModels.find((m) => m.id === config?.default_model);
    const currentStillValid = availableModels.some((m) => m.id === selectedModel);

    if (requested && !modelInitialized.current) {
      setSelectedModel(requested.id);
      modelTouched.current = true;
      modelInitialized.current = true;
      return;
    }

    if (configuredDefault && (!selectedModel || !currentStillValid || !modelTouched.current)) {
      setSelectedModel(configuredDefault.id);
      modelInitialized.current = true;
    } else if (!configuredDefault && !currentStillValid) {
      setSelectedModel('');
    }
  }, [availableModels, config?.default_model, entryContext.modelId, selectedModel]);

  useEffect(() => {
    if (!selectedModelConfig) {
      setSelectedQuality('');
      // Model selection is committed in a separate effect. Preserve a
      // deep-link ratio through that intermediate render and validate it only
      // after the concrete model contract is available.
      return;
    }
    const qualities = selectedModelConfig.supported_qualities ?? [];
    if (!qualities.includes(selectedQuality)) {
      setSelectedQuality(qualities.includes('standard') ? 'standard' : (qualities[0] ?? ''));
    }
    if (entryConfig?.ratio_mode === 'fixed' && entryConfig.fixed_ratio) {
      setSelectedRatio(entryConfig.fixed_ratio);
      return;
    }
    const ratios = selectedModelConfig.supported_ratios ?? [];
    if (entryConfig?.ratio_mode === 'selectable' && !ratios.includes(selectedRatio ?? '')) {
      setSelectedRatio(ratios.includes('1:1') ? '1:1' : (ratios[0] ?? null));
    }
  }, [entryConfig, selectedModelConfig, selectedQuality, selectedRatio]);

  // ---- Points cost ----
  const configuredPricingQuote = selectedModel
    ? getGenerationPricingQuote(config, selectedModel, selectedQuality)
    : null;
  const configuredPointsCost = configuredPricingQuote?.expected_cost_points ?? null;
  const displayedPointsCost = chatCostOverride ?? configuredPointsCost;

  const resolveGenerationPointsCost = useCallback(() => (
    selectedModel
      ? getGenerationPricingQuote(config, selectedModel, selectedQuality)?.expected_cost_points ?? null
      : null
  ), [config, selectedModel, selectedQuality]);

  // ---- Pre-fill template from URL ----
  useEffect(() => {
    const templateId = entryContext.templateId;
    if (!templateId) return;
    if (!appTemplates.length) return;
    const tmpl = appTemplates.find((t) => t.id === templateId);
    if (tmpl) {
      setPrompt(tmpl.default_prompt ?? '');
      if (tmpl.recommend_style_id) setSelectedStyle(tmpl.recommend_style_id);
      if (tmpl.recommend_ratio) setSelectedRatio(tmpl.recommend_ratio);
      setSelectedTemplate(tmpl.id);
    }
  }, [appTemplates, entryContext.templateId]);

  // ---- Auto-trigger after paywall redirect ----
  useEffect(() => {
    if (!config) return;
    if (!user) return;
    const raw = sessionStorage.getItem('paySource');
    if (!raw) return;
    sessionStorage.removeItem('paySource');
    if (shouldResumeGenerationAfterPayment(raw, prompt)) {
      void handleGenerate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, user]);

  // ---- Toast helpers ----
  const showGenToast = useCallback((msg: string) => {
    if (genToastTimer.current) clearTimeout(genToastTimer.current);
    setGenToast(msg);
    genToastTimer.current = setTimeout(() => setGenToast(null), 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (genToastTimer.current) clearTimeout(genToastTimer.current);
    };
  }, []);

  useEffect(() => {
    img2imgReferenceImagesRef.current = img2imgReferenceImages;
  }, [img2imgReferenceImages]);

  useEffect(() => () => {
    img2imgReferenceImagesRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
  }, []);

  const readyImg2ImgReferenceUrls = useMemo(
    () => img2imgReferenceImages
      .map((image) => image.remoteUrl)
      .filter((url): url is string => !!url),
    [img2imgReferenceImages],
  );

  const img2imgUploadInProgress = img2imgReferenceImages.some((image) => image.uploading);

  // ---- Template select ----
  const handleTemplateSelect = useCallback(
    (templateId: string, defaultPrompt: string, styleId: string, ratio: string, category?: string, categoryTitleKey?: string) => {
      setSelectedTemplate(templateId);
      setPrompt(defaultPrompt);
      if (styleId) setSelectedStyle(styleId);
      if (ratio) setSelectedRatio(ratio);
      if (category) setTemplateCategory(category);
      if (categoryTitleKey) setTemplateCategoryTitleKey(categoryTitleKey);
      pushEvent('generate_config_template', user?.userId, { templateId, category, styleId, ratio });
    },
    [user?.userId],
  );

  // ---- AI Help (expand prompt) ----
  const handleAIHelp = useCallback(async (currentPrompt: string): Promise<string> => {
    return (await expandEntryPrompt(currentPrompt)) ?? currentPrompt;
  }, [expandEntryPrompt]);

  const handleInlineAIHelp = useCallback(async (
    currentPrompt: string,
    onExpanded: (expandedPrompt: string) => void,
  ) => {
    const requestSeq = inlineAIHelpRequestSeqRef.current + 1;
    inlineAIHelpRequestSeqRef.current = requestSeq;
    setInlineAIHelpLoading(true);
    setInlineAIHelpError(null);
    try {
      const expanded = await expandEntryPrompt(currentPrompt);
      if (requestSeq === inlineAIHelpRequestSeqRef.current && expanded != null) {
        onExpanded(expanded);
      }
    } catch (err) {
      if (requestSeq !== inlineAIHelpRequestSeqRef.current) return;
      const message = err instanceof ApiError
        ? err.message
        : t('gen_aiHelpFailed', 'AI 帮写失败，请稍后重试');
      setInlineAIHelpError(message);
      showGenToast(message);
    } finally {
      if (requestSeq === inlineAIHelpRequestSeqRef.current) {
        setInlineAIHelpLoading(false);
      }
    }
  }, [expandEntryPrompt, showGenToast, t]);

  const removeImg2ImgReferenceImage = useCallback((imageId: string) => {
    setImg2imgReferenceImages((images) => {
      const removed = images.find((image) => image.id === imageId);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      const nextImages = images.filter((image) => image.id !== imageId);
      img2imgReferenceImagesRef.current = nextImages;
      return nextImages;
    });
  }, []);

  const handleImg2ImgReferenceFiles = useCallback((files: File[]) => {
    if (!files.length) return;
    const supportedFiles = files.filter((file) => (
      IMG2IMG_ALLOWED_REFERENCE_TYPES.has(file.type) ||
      (file.type === '' && IMG2IMG_ALLOWED_REFERENCE_EXTENSIONS.test(file.name))
    ));
    if (supportedFiles.length < files.length) {
      showGenToast(t('img2img_onlyJpgPng'));
    }
    if (!supportedFiles.length) return;

    const currentImages = img2imgReferenceImagesRef.current;
    const remainingSlots = IMG2IMG_REFERENCE_LIMIT - currentImages.length;
    if (remainingSlots <= 0) {
      showGenToast(t('img2img_imageLimit', { count: IMG2IMG_REFERENCE_LIMIT }));
      return;
    }

    const selectedFiles = supportedFiles.slice(0, remainingSlots);
    if (supportedFiles.length > remainingSlots) {
      showGenToast(t('img2img_imageLimit', { count: IMG2IMG_REFERENCE_LIMIT }));
    }

    const pendingImages = selectedFiles.map((file) => ({
      id: randomUUID(),
      previewUrl: URL.createObjectURL(file),
      uploading: true,
    }));
    img2imgReferenceImagesRef.current = [...currentImages, ...pendingImages];
    setImg2imgReferenceImages((images) => [...images, ...pendingImages]);

    selectedFiles.forEach((file, index) => {
      const imageId = pendingImages[index].id;
      void uploadReference(file)
        .then((res) => {
          setImg2imgReferenceImages((images) => images.map((image) => (
            image.id === imageId
              ? { ...image, remoteUrl: res.url, uploading: false }
              : image
          )));
          track.referenceUploadSuccess({ source: 'generate_img2img_multi', count: 1 });
        })
        .catch(() => {
          setImg2imgReferenceImages((images) => {
            const failed = images.find((image) => image.id === imageId);
            if (failed) URL.revokeObjectURL(failed.previewUrl);
            const nextImages = images.filter((image) => image.id !== imageId);
            img2imgReferenceImagesRef.current = nextImages;
            return nextImages;
          });
          showGenToast(t('gen_referenceUploadFailed'));
          track.referenceUploadFail({ source: 'generate_img2img_multi', reason: 'upload_failed' });
        });

      pushEvent('generate_reference_upload', user?.userId, {
        fileSize: file.size,
        fileType: file.type,
        target: 'img2img_multi',
      });
    });
  }, [showGenToast, t, user?.userId]);

  // ---- File change handler ----
  const handleFileChange = useCallback(async (
    e: ChangeEvent<HTMLInputElement>,
    target: 'primary' | 'secondary' = 'primary',
  ) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (target === 'primary' && currentMode === 'img2img') {
      handleImg2ImgReferenceFiles(files);
      return;
    }
    const file = files[0];
    if (!file) return;
    const uploadSeq = target === 'primary'
      ? primaryReferenceUploadSeqRef.current + 1
      : secondaryReferenceUploadSeqRef.current + 1;
    if (target === 'primary') {
      primaryReferenceUploadSeqRef.current = uploadSeq;
    } else {
      secondaryReferenceUploadSeqRef.current = uploadSeq;
    }

    // Store as data URL for preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const isStale = target === 'primary'
        ? primaryReferenceUploadSeqRef.current !== uploadSeq
        : secondaryReferenceUploadSeqRef.current !== uploadSeq;
      if (isStale) return;
      const dataUrl = event.target?.result as string;
      if (target === 'primary') {
        if (referencePreviewUrlRef.current) URL.revokeObjectURL(referencePreviewUrlRef.current);
        tryonModelUploadSeq.current += 1;
        setTryonModelUploading(false);
        setReferenceImage(dataUrl);
        setSelectedTryonModelId(null);
      } else {
        setSecondaryImage(dataUrl);
      }
    };
    reader.readAsDataURL(file);

    // Upload for real API
    try {
      const res = await uploadReference(file);
      const isStale = target === 'primary'
        ? primaryReferenceUploadSeqRef.current !== uploadSeq
        : secondaryReferenceUploadSeqRef.current !== uploadSeq;
      if (isStale) return;
      if (target === 'primary') {
        setReferenceImageUrl(res.url);
        if (entryConfig?.ratio_mode === 'source' && res.recommended_ratio) {
          setSelectedRatio(res.recommended_ratio);
        }
      } else {
        setSecondaryImageUrl(res.url);
      }
      track.referenceUploadSuccess({ source: `generate_${target}`, count: 1 });
    } catch {
      const isStale = target === 'primary'
        ? primaryReferenceUploadSeqRef.current !== uploadSeq
        : secondaryReferenceUploadSeqRef.current !== uploadSeq;
      if (isStale) return;
      track.referenceUploadFail({ source: `generate_${target}`, reason: 'upload_failed' });
      if (target === 'primary') {
        setReferenceImage(null);
        setReferenceImageUrl(undefined);
      } else {
        setSecondaryImage(null);
        setSecondaryImageUrl(undefined);
      }
    }

    pushEvent('generate_reference_upload', user?.userId, {
      fileSize: file.size,
      fileType: file.type,
      target,
    });
  }, [currentMode, entryConfig?.ratio_mode, handleImg2ImgReferenceFiles, user?.userId]);

  // ---- iOS native photo pick for primary reference ----
  const handleNativePhotoPick = useCallback(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      const uploadSeq = primaryReferenceUploadSeqRef.current + 1;
      primaryReferenceUploadSeqRef.current = uploadSeq;
      window.__NATIVE_BRIDGE__?.pickPhoto?.()
        .then(async ({ base64, mimeType }) => {
          if (primaryReferenceUploadSeqRef.current !== uploadSeq) return;
          const binary = atob(base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const blob = new Blob([bytes], { type: mimeType });
          const dataUrl = `data:${mimeType};base64,${base64}`;
          if (currentModeRef.current === 'img2img') {
            const file = new File([blob], 'reference.jpg', { type: mimeType });
            handleImg2ImgReferenceFiles([file]);
            return;
          }
          if (referencePreviewUrlRef.current) URL.revokeObjectURL(referencePreviewUrlRef.current);
          tryonModelUploadSeq.current += 1;
          setTryonModelUploading(false);
          setReferenceImage(dataUrl);
          setSelectedTryonModelId(null);
          const file = new File([blob], 'reference.jpg', { type: mimeType });
          try {
            const res = await uploadReference(file);
            if (primaryReferenceUploadSeqRef.current !== uploadSeq) return;
            setReferenceImageUrl(res.url);
            if (currentModeRef.current === 'restore' && res.recommended_ratio) {
              setSelectedRatio(res.recommended_ratio);
            }
            track.referenceUploadSuccess({ source: 'generate_primary_ios', count: 1 });
          } catch {
            if (primaryReferenceUploadSeqRef.current !== uploadSeq) return;
            track.referenceUploadFail({ source: 'generate_primary_ios', reason: 'upload_failed' });
            setReferenceImage(null);
            setReferenceImageUrl(undefined);
          }
        })
        .catch(() => {});
    } else {
      fileInputRef.current?.click();
    }
  }, [handleImg2ImgReferenceFiles]);

  // ---- iOS native photo pick for secondary reference (tryon clothing) ----
  const handleNativePhotoPickSecondary = useCallback(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      const uploadSeq = secondaryReferenceUploadSeqRef.current + 1;
      secondaryReferenceUploadSeqRef.current = uploadSeq;
      window.__NATIVE_BRIDGE__?.pickPhoto?.()
        .then(async ({ base64, mimeType }) => {
          if (secondaryReferenceUploadSeqRef.current !== uploadSeq) return;
          const binary = atob(base64);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const blob = new Blob([bytes], { type: mimeType });
          const dataUrl = `data:${mimeType};base64,${base64}`;
          setSecondaryImage(dataUrl);
          const file = new File([blob], 'clothing.jpg', { type: mimeType });
          try {
            const res = await uploadReference(file);
            if (secondaryReferenceUploadSeqRef.current !== uploadSeq) return;
            setSecondaryImageUrl(res.url);
            track.referenceUploadSuccess({ source: 'generate_secondary_ios', count: 1 });
          } catch {
            if (secondaryReferenceUploadSeqRef.current !== uploadSeq) return;
            track.referenceUploadFail({ source: 'generate_secondary_ios', reason: 'upload_failed' });
            setSecondaryImage(null);
            setSecondaryImageUrl(undefined);
          }
        })
        .catch(() => {});
    } else {
      secondaryFileInputRef.current?.click();
    }
  }, []);

  // ---- Tryon model select ----
  const handleSelectTryonModel = useCallback((model: (typeof tryonModels)[number]) => {
    const uploadSeq = tryonModelUploadSeq.current + 1;
    tryonModelUploadSeq.current = uploadSeq;
    setSelectedTryonModelId(model.id);
    setReferenceImage(model.image);
    setReferenceImageUrl(undefined);
    setTryonModelUploading(true);
    pushEvent('generate_tryon_model_select', user?.userId, { modelId: model.id, gender: model.gender });

    void (async () => {
      try {
        const response = await fetch(model.image, { mode: 'cors' });
        if (!response.ok) throw new Error('tryon model fetch failed');
        const blob = await response.blob();
        if (!blob.type.startsWith('image/')) throw new Error('tryon model is not an image');
        const file = new File([blob], `${model.id}.jpg`, { type: blob.type || 'image/jpeg' });
        const res = await uploadReference(file);
        if (tryonModelUploadSeq.current !== uploadSeq) return;
        setReferenceImageUrl(res.url);
        track.referenceUploadSuccess({ source: 'generate_tryon_builtin_model', count: 1 });
      } catch {
        if (tryonModelUploadSeq.current !== uploadSeq) return;
        setReferenceImage(null);
        setReferenceImageUrl(undefined);
        setSelectedTryonModelId(null);
        showGenToast(t('gen_referenceUploadFailed'));
        track.referenceUploadFail({ source: 'generate_tryon_builtin_model', reason: 'upload_failed' });
      } finally {
        if (tryonModelUploadSeq.current === uploadSeq) {
          setTryonModelUploading(false);
        }
      }
    })();
  }, [showGenToast, t, user?.userId]);

  // ---- Can generate check ----
  const canGenerate = useCallback(() => {
    if (
      !modelSelectionMatchesGenerationEntry(
        selectedModelConfig,
        entryConfig,
        selectedQuality,
        selectedRatio,
        selectionReferenceImageCount,
      )
      || configuredPointsCost === null
      || (capacityReady && activeLimit > 0 && activeCount >= activeLimit)
    ) return false;
    switch (currentMode) {
      case 'text2img':
        return prompt.trim().length > 0;
      case 'img2img':
        return prompt.trim().length > 0 && readyImg2ImgReferenceUrls.length > 0 && !img2imgUploadInProgress;
      case 'edit':
        return editPrompt.trim().length > 0 && !!referenceImage && !!referenceImageUrl;
      case 'restore':
        return !!referenceImage && !!referenceImageUrl;
      case 'tryon':
        return !tryonModelUploading && !!referenceImage && !!referenceImageUrl && !!secondaryImage && !!secondaryImageUrl;
      default:
        return false;
    }
  }, [selectedModelConfig, entryConfig, selectedQuality, selectedRatio, selectionReferenceImageCount, configuredPointsCost, capacityReady, activeLimit, activeCount, currentMode, prompt, editPrompt, readyImg2ImgReferenceUrls.length, img2imgUploadInProgress, referenceImage, referenceImageUrl, secondaryImage, secondaryImageUrl, tryonModelUploading]);

  // ---- Main generate handler ----
  const handleGenerate = useCallback(async (overridePrompt?: string, opts?: GenerateOptions) => {
    if (capacityReady && activeLimit > 0 && activeCount >= activeLimit) {
      showGenToast(t('gen_concurrencyLimitReached', '并发任务已满，请等待已有任务完成'));
      return;
    }
    if (createInFlightRef.current) {
      showGenToast(t('gen_submissionInFlight', '正在提交上一项任务，请稍候'));
      return;
    }
    const submissionId = randomUUID();
    const effectivePrompt = overridePrompt ?? prompt;
    const effectiveMode = opts?.forceMode ?? currentMode;
    const forcedRefUrls = opts?.forceRefUrls ?? (opts?.forceRefUrl ? [opts.forceRefUrl] : undefined);
    const modeReferenceUrls = effectiveMode === 'img2img' ? readyImg2ImgReferenceUrls : undefined;
    const effectiveRefUrls = forcedRefUrls ?? modeReferenceUrls ?? (referenceImageUrl ? [referenceImageUrl] : []);
    const effectiveRefUrl = effectiveRefUrls[0];
    const effectiveSecondaryUrl = effectiveMode === 'tryon'
      ? secondaryImageUrl
      : ((effectiveMode === 'edit' || effectiveMode === 'img2img') ? effectiveRefUrls[1] : undefined);
    const effectiveAdditionalUrls = effectiveMode === 'img2img'
      ? effectiveRefUrls.slice(2)
      : (forcedRefUrls?.slice(2) ?? []);
    const sourceEntry = opts?.sourceEntry ?? 'generate_page';
    const entryId = getGenerateEntryId(effectiveMode, opts?.sourceEntry);
    const generationOperation = getGenerationOperation(effectiveMode);
    const effectiveEntryConfig = getGenerationEntryConfig(config, effectiveMode);
    const effectiveModelConfig = appModels.find((model) => model.id === selectedModel);
    const effectiveReferenceImageCount = [
      effectiveRefUrl,
      effectiveSecondaryUrl,
      ...effectiveAdditionalUrls,
    ].filter(Boolean).length;
    const effectiveSelectionIsValid = modelSelectionMatchesGenerationEntry(
      effectiveModelConfig,
      effectiveEntryConfig,
      selectedQuality,
      selectedRatio,
      effectiveReferenceImageCount,
    );
    const effectivePricingQuote = effectiveSelectionIsValid
      ? getGenerationPricingQuote(config, selectedModel, selectedQuality)
      : null;
    const effectivePointsCost = effectivePricingQuote?.expected_cost_points ?? null;

    if (!user) { navigate('/login?redirect=/generate'); return; }
    if (!effectiveSelectionIsValid || !effectivePricingQuote || !selectedRatio) {
      setTaskStatus('failed');
      setTaskReasonCode('CONFIG_ERROR');
      return;
    }
    if ((user.points ?? 0) < effectivePointsCost) {
      setPointsModalCost(effectivePointsCost);
      setShowPointsModal(true);
      return;
    }

    // Mode-specific guards
    if (!opts) {
      if (currentMode === 'img2img' && (readyImg2ImgReferenceUrls.length === 0 || img2imgUploadInProgress)) return;
      if (currentMode === 'edit' && (!referenceImage || !referenceImageUrl)) return;
      if (currentMode === 'restore' && (!referenceImage || !referenceImageUrl)) return;
      if (currentMode === 'tryon' && (tryonModelUploading || !referenceImage || !referenceImageUrl || !secondaryImage || !secondaryImageUrl)) return;
      if (currentMode === 'text2img' && !effectivePrompt.trim()) return;
    } else {
      // Override mode - just check basic validity
      if (effectiveMode === 'text2img' && !effectivePrompt.trim()) return;
      if ((effectiveMode === 'img2img' || effectiveMode === 'edit') && !effectiveRefUrl) {
        showGenToast(t('gen_referenceUploadFailed'));
        return;
      }
    }

    // VIP style check
    const style = appStyles.find((s) => s.id === selectedStyle);
    if (style?.is_vip && !user.isVip) {
      track.vipUpgradeClick('generate_page');
      setShowVipSheet(true);
      return;
    }

    createInFlightRef.current = submissionId;
    setIsGenerating(true);
    setActiveGenerationId(null);
    setResult(null);
    setShowInlineMembershipCta(false);
    setTaskStatus('queued');
    setTaskReasonCode(null);
    const imageCount = [
      effectiveRefUrl,
      effectiveSecondaryUrl,
      ...effectiveAdditionalUrls,
    ].filter(Boolean).length;
    const reportPayload = {
      entry_id: entryId,
      mode: effectiveMode,
      model: selectedModel,
      quality: selectedQuality,
      generation_operation: generationOperation,
      source: sourceEntry,
      has_reference: !!effectiveRefUrl,
      image_count: imageCount,
    };

    pushEvent('generate_start', user.userId, {
      mode: effectiveMode,
      model: selectedModel,
      quality: selectedQuality,
      hasStyle: !!selectedStyle,
      hasTemplate: !!selectedTemplate,
      hasReference: !!effectiveRefUrl,
    });
    track.generateStart({
      template: selectedTemplate,
      style: selectedStyle,
      model: selectedModel,
      ratio: selectedRatio,
      mode: effectiveMode,
      has_reference: !!effectiveRefUrl,
      image_count: imageCount,
      source: sourceEntry,
      entry_id: entryId,
      generation_operation: generationOperation,
    });
    track.generateSubmit(reportPayload);

    let activeGenId: string | undefined;
    let idempotencyKey = opts?.retryIdempotencyKey ?? randomUUID();
    try {
      const requestPrompt = effectivePrompt.trim() || editPrompt.trim();

      const requestPayload: Parameters<typeof createGeneration>[0] = {
        prompt: requestPrompt,
        style_id: selectedStyle || '',
        ratio: selectedRatio,
        reference_image_url: effectiveRefUrl,
        secondary_image_url: effectiveSecondaryUrl,
        additional_image_urls: effectiveAdditionalUrls,
        tool_options: effectiveMode === 'restore'
          ? { restore_strength: restoreStrength }
          : effectiveMode === 'tryon'
            ? { pose: selectedPose as 'natural' | 'front' | 'side' | 'cross' }
            : undefined,
        idempotency_key: idempotencyKey,
        model_id: selectedModel,
        quality: selectedQuality,
        template_id: selectedTemplate || undefined,
        mode: effectiveMode,
        tool_id: effectiveMode,
        entry_id: entryId,
        source_entry: 'consumer_app',
        generation_operation: generationOperation,
        ...effectivePricingQuote,
      };

      const unresolved = findUnresolvedLocalPending(user.userId, requestPayload);
      if (unresolved && unresolved.idempotency_key !== idempotencyKey) {
        const discardedClientRequestId = idempotencyKey;
        idempotencyKey = unresolved.idempotency_key;
        dispatchSubmission({ type: 'ack_unknown', clientRequestId: idempotencyKey });
        restartLocalPendingReconcile(user.userId, idempotencyKey);
        if (sourceEntry === 'chat_result') {
          setChatSubmissionRejection({
            id: `${idempotencyKey}:ack_unknown`,
            clientRequestId: idempotencyKey,
            discardClientRequestId: discardedClientRequestId,
            prompt: effectivePrompt.trim(),
            code: 'ACK_UNKNOWN',
          });
        }
        showGenToast(t('gen_submissionConfirming', '正在确认任务是否已提交，请勿重复发送'));
        return;
      }

      dispatchSubmission({ type: 'sending', clientRequestId: idempotencyKey });

      writeLocalPending({
        user_id: user.userId,
        idempotency_key: idempotencyKey,
        payload: requestPayload,
        source: sourceEntry,
        created_at: Date.now(),
        updated_at: Date.now(),
      });
      const created = opts?.resumeGenId ? null : await createGeneration(requestPayload);
      if (created?.points_balance_after != null) {
        setPoints(created.points_balance_after);
      }
      const genId = opts?.resumeGenId ?? created?.gen_id;
      if (!genId) throw new ApiError('GEN_001', '生成任务创建失败', 500);
      if (created) acceptCreateAck(created, requestPayload);
      dispatchSubmission({ type: 'accepted', clientRequestId: idempotencyKey, genId });
      activeGenId = genId;
      setActiveGenerationId(genId);
      activeGenerationRef.current = genId;
      writeLocalPending({
        user_id: user.userId,
        gen_id: genId,
        poll_timeout_seconds: created?.poll_timeout_seconds,
        idempotency_key: idempotencyKey,
        payload: requestPayload,
        source: sourceEntry,
        created_at: Date.now(),
        updated_at: Date.now(),
      });
      setIsGenerating(false);
      if (createInFlightRef.current === submissionId) {
        createInFlightRef.current = null;
      }
      setTaskStatus(null);
      showGenToast(t('gen_generationContinuesInBackground', '任务已提交，可继续生成下一张'));

      // Every entry is ACK-bounded. The global TaskStore owns terminal
      // synchronization, notifications, balance projection and recovery.
      setActiveGenerationId(null);
      activeGenerationRef.current = null;
      return;
    } catch (err) {
      const isCurrentTask = !activeGenId || activeGenerationRef.current === activeGenId;
      if (err instanceof ApiError) {
        if (err.code === 'CONFIG_STALE') {
          clearLocalPendingByIdempotencyKey(user.userId, idempotencyKey);
          dispatchSubmission({ type: 'rejected', clientRequestId: idempotencyKey, code: err.code });
          if (sourceEntry === 'chat_result') {
            setChatSubmissionRejection({
              id: `${idempotencyKey}:${err.code}`,
              clientRequestId: idempotencyKey,
              prompt: effectivePrompt.trim(),
              code: err.code,
            });
          }
          if (isCurrentTask) {
            setActiveGenerationId(null);
            setTaskStatus(null);
          }
          const pricingConfigRefreshed = await refreshModelsConfig();
          track.generateFail(err.code, { mode: effectiveMode, model: selectedModel });
          track.generationFailed(err.code, reportPayload);
          showGenToast(t(
            pricingConfigRefreshed
              ? 'gen_pricingConfigUpdated'
              : 'gen_pricingConfigRefreshFailed',
            pricingConfigRefreshed
              ? '积分配置已更新，请确认最新积分后重新提交'
              : '积分配置刷新失败，本次任务未提交，请稍后重试',
          ));
        } else if (err.code === 'GEN_ACTIVE_LIMIT') {
          clearLocalPendingByIdempotencyKey(user.userId, idempotencyKey);
          dispatchSubmission({ type: 'rejected', clientRequestId: idempotencyKey, code: err.code });
          const nextActiveCount = Number(err.details?.active_count);
          const nextActiveLimit = Number(err.details?.active_limit);
          if (Number.isFinite(nextActiveCount) && Number.isFinite(nextActiveLimit)) {
            applyCapacity(nextActiveCount, nextActiveLimit);
          }
          if (sourceEntry === 'chat_result') {
            setChatSubmissionRejection({
              id: `${idempotencyKey}:${err.code}`,
              clientRequestId: idempotencyKey,
              prompt: effectivePrompt.trim(),
              code: err.code,
            });
          }
          if (isCurrentTask) {
            setActiveGenerationId(null);
            setTaskStatus(null);
          }
          track.generateFail(err.code, { mode: effectiveMode, model: selectedModel });
          track.generationFailed(err.code, reportPayload);
        } else if (err.code === 'QUOTA_001' || err.code === 'QUOTA_002') {
          if (!activeGenId) {
            clearLocalPendingByIdempotencyKey(user.userId, idempotencyKey);
          }
          clearLocalPending(activeGenId);
          dispatchSubmission({ type: 'rejected', clientRequestId: idempotencyKey, code: err.code });
          if (isCurrentTask) setActiveGenerationId(null);
          track.generateFail(err.code, { mode: effectiveMode, model: selectedModel });
          track.generationFailed(err.code, reportPayload);
          sessionStorage.setItem('paySource', JSON.stringify({
            route: '/generate',
            action: 'gen_when_quota_empty',
            params: {},
          }));
          navigate('/membership');
        } else if (isPollTimeout(err)) {
          if (isCurrentTask) setTaskStatus(null);
          track.generateFail('foreground_poll_timeout_background', { mode: effectiveMode, model: selectedModel });
          track.generationFailed('foreground_poll_timeout_background', reportPayload);
          showGenToast(t('gen_generationContinuesInBackground', '生成仍在后台进行，请稍后回来查看'));
        } else if (
          !activeGenId
          && err.details?.submission_state === 'rejected_before_commit'
        ) {
          clearLocalPendingByIdempotencyKey(user.userId, idempotencyKey);
          dispatchSubmission({ type: 'rejected', clientRequestId: idempotencyKey, code: err.code });
          if (sourceEntry === 'chat_result') {
            setChatSubmissionRejection({
              id: `${idempotencyKey}:${err.code}`,
              clientRequestId: idempotencyKey,
              prompt: effectivePrompt.trim(),
              code: err.code,
            });
          }
          if (isCurrentTask) {
            setActiveGenerationId(null);
            setTaskStatus(null);
          }
          track.generateFail(err.code, { mode: effectiveMode, model: selectedModel });
          track.generationFailed(err.code, reportPayload);
          showGenToast(t('gen_submitNotAccepted', '任务未提交，请检查后重试'));
        } else if (!activeGenId && (err.status === 0 || err.status >= 500 || err.code === 'PARSE_ERROR')) {
          dispatchSubmission({ type: 'ack_unknown', clientRequestId: idempotencyKey });
          markLocalPendingForReconcile(user.userId, idempotencyKey, 'ack_unknown');
          if (sourceEntry === 'chat_result') {
            setChatSubmissionRejection({
              id: `${idempotencyKey}:ack_unknown`,
              clientRequestId: idempotencyKey,
              prompt: effectivePrompt.trim(),
              code: 'ACK_UNKNOWN',
            });
          }
          if (isCurrentTask) {
            setActiveGenerationId(null);
            setTaskStatus(null);
          }
          track.generateFail('create_ack_unknown', { mode: effectiveMode, model: selectedModel });
          track.generationFailed('create_ack_unknown', reportPayload);
          showGenToast(t('gen_submissionConfirming', '正在确认任务是否已提交，请勿重复发送'));
        } else {
          if (!activeGenId && err.status >= 400 && err.status < 500) {
            clearLocalPendingByIdempotencyKey(user.userId, idempotencyKey);
            dispatchSubmission({ type: 'rejected', clientRequestId: idempotencyKey, code: err.code });
          }
          clearLocalPending(activeGenId);
          if (isCurrentTask) {
            setActiveGenerationId(null);
            setTaskStatus('failed');
          }
          track.generateFail(err.code, { mode: effectiveMode, model: selectedModel });
          track.generationFailed(err.code, reportPayload);
          showGenToast(t('gen_genFailed'));
        }
      } else {
        if (!activeGenId) {
          dispatchSubmission({ type: 'ack_unknown', clientRequestId: idempotencyKey });
          markLocalPendingForReconcile(user.userId, idempotencyKey, 'ack_unknown');
          if (sourceEntry === 'chat_result') {
            setChatSubmissionRejection({
              id: `${idempotencyKey}:ack_unknown`,
              clientRequestId: idempotencyKey,
              prompt: effectivePrompt.trim(),
              code: 'ACK_UNKNOWN',
            });
          }
          if (isCurrentTask) {
            setActiveGenerationId(null);
            setTaskStatus(null);
          }
          showGenToast(t('gen_submissionConfirming', '正在确认任务是否已提交，请勿重复发送'));
        } else {
          clearLocalPending(activeGenId);
          if (isCurrentTask) {
            setActiveGenerationId(null);
            setTaskStatus('failed');
          }
          showGenToast(t('gen_genFailed'));
        }
        track.generateFail('unknown', { mode: effectiveMode, model: selectedModel });
        track.generationFailed('unknown', reportPayload);
      }
    } finally {
      if (!activeGenId || activeGenerationRef.current === activeGenId) {
        activeGenerationRef.current = null;
      }
      if (createInFlightRef.current === submissionId) {
        createInFlightRef.current = null;
        setIsGenerating(false);
      }
    }
  }, [
    prompt,
    editPrompt,
    user,
    currentMode,
    config,
    appModels,
    referenceImage,
    referenceImageUrl,
    readyImg2ImgReferenceUrls,
    img2imgUploadInProgress,
    secondaryImage,
    secondaryImageUrl,
    tryonModelUploading,
    selectedPose,
    restoreStrength,
    selectedStyle,
    selectedRatio,
    selectedModel,
    selectedQuality,
    selectedTemplate,
    appStyles,
    acceptCreateAck,
    applyCapacity,
    activeCount,
    activeLimit,
    capacityReady,
    navigate,
    showGenToast,
    t,
  ]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;

    const reconcilePendingSubmissions = async () => {
      if (cancelled || recoveringPendingRef.current || document.visibilityState === 'hidden') return;
      recoveringPendingRef.current = true;
      try {
        const now = Date.now();
        const localPendings = readLocalPendings(user.userId);
        for (const localPending of localPendings) {
          if (cancelled) return;
          if (localPending.submission_state === 'sending') {
            // A request that is still owned by this page must never race its own
            // Create with a speculative GET.  After a reload there is no live
            // fetch owner, so reclassify the persisted entry and reconcile it on
            // the next bounded interval.
            if (!createInFlightRef.current) {
              markLocalPendingForReconcile(
                user.userId,
                localPending.idempotency_key,
                'ack_unknown',
              );
            }
            continue;
          }
          if (!isLocalPendingReadyForReconcile(
            localPending,
            now,
            SUBMISSION_RECONCILE_MAX_ATTEMPTS,
          )) continue;

          const reconciling = markLocalPendingForReconcile(
            user.userId,
            localPending.idempotency_key,
            'reconciling',
          );
          if (!reconciling) continue;
          dispatchSubmission({
            type: 'reconciling',
            clientRequestId: localPending.idempotency_key,
          });
          try {
            const submission = await getGenerationSubmission(localPending.idempotency_key);
            if (cancelled) return;
            applyCapacity(submission.active_count, submission.active_limit);
            if (submission.submission_state === 'accepted' && submission.gen_id) {
              const created = {
                gen_id: submission.gen_id,
                client_request_id: localPending.idempotency_key,
                status: submission.status ?? 'queued',
                status_version: submission.status_version ?? 1,
                active_count: submission.active_count,
                active_limit: submission.active_limit,
                points_balance_after: submission.points_balance,
                balance_version: submission.balance_version,
              } as const;
              acceptCreateAck(created, localPending.payload);
              writeLocalPending({
                ...localPending,
                gen_id: submission.gen_id,
                submission_state: 'accepted',
              });
              dispatchSubmission({
                type: 'accepted',
                clientRequestId: localPending.idempotency_key,
                genId: submission.gen_id,
              });
              await refreshGenerationTasks();
              continue;
            }

            const attempts = reconciling.reconcile_attempts ?? 1;
            if (
              submission.submission_state === 'not_found'
              && attempts >= SUBMISSION_RECONCILE_MAX_ATTEMPTS
            ) {
              clearLocalPendingByIdempotencyKey(user.userId, localPending.idempotency_key);
              dispatchSubmission({
                type: 'rejected',
                clientRequestId: localPending.idempotency_key,
                code: 'GEN_SUBMISSION_NOT_FOUND',
              });
              if (localPending.source === 'chat_result') {
                setChatSubmissionRejection({
                  id: `${localPending.idempotency_key}:GEN_SUBMISSION_NOT_FOUND`,
                  clientRequestId: localPending.idempotency_key,
                  prompt: localPending.payload.prompt,
                  code: 'GEN_SUBMISSION_NOT_FOUND',
                });
              }
              continue;
            }
            markLocalPendingForReconcile(
              user.userId,
              localPending.idempotency_key,
              'ack_unknown',
              SUBMISSION_RECONCILE_INTERVAL_MS,
            );
            dispatchSubmission({
              type: 'ack_unknown',
              clientRequestId: localPending.idempotency_key,
            });
          } catch (err) {
            markLocalPendingForReconcile(
              user.userId,
              localPending.idempotency_key,
              'ack_unknown',
              SUBMISSION_RECONCILE_INTERVAL_MS,
            );
            dispatchSubmission({
              type: 'ack_unknown',
              clientRequestId: localPending.idempotency_key,
            });
            console.warn('[generation-outbox] read-only ACK reconciliation failed', {
              idempotency_key: localPending.idempotency_key,
              code: err instanceof ApiError ? err.code : 'ACK_UNKNOWN',
            });
          }
        }
      } finally {
        recoveringPendingRef.current = false;
      }
    };

    const reconcileWhenVisible = () => {
      if (document.visibilityState === 'visible') void reconcilePendingSubmissions();
    };
    void reconcilePendingSubmissions();
    const timer = window.setInterval(
      () => { void reconcilePendingSubmissions(); },
      SUBMISSION_RECONCILE_INTERVAL_MS,
    );
    window.addEventListener('online', reconcileWhenVisible);
    document.addEventListener('visibilitychange', reconcileWhenVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('online', reconcileWhenVisible);
      document.removeEventListener('visibilitychange', reconcileWhenVisible);
    };
  }, [acceptCreateAck, applyCapacity, refreshGenerationTasks, user]);

  // ---- Chat-style send (text2img) ----
  const handleChatSend = useCallback((
    text: string,
    refImageUrls?: string[],
    clientRequestId?: string,
  ) => {
    setPrompt(text);
    setChatCostOverride(refImageUrls?.length ? resolveGenerationPointsCost() : null);
    requestAnimationFrame(() => {
      void handleGenerate(text, refImageUrls?.length
        ? {
          forceMode: 'edit',
          forceRefUrls: refImageUrls,
          sourceEntry: 'chat_result',
          retryIdempotencyKey: clientRequestId,
        }
        : {
          forceMode: 'text2img',
          sourceEntry: 'chat_result',
          retryIdempotencyKey: clientRequestId,
        });
    });
  }, [handleGenerate, resolveGenerationPointsCost]);

  // ---- Regenerate ----
  const handleRegenerate = useCallback(() => {
    setResult(null);
    setTaskStatus(null);
    setTimeout(() => { void handleGenerate(undefined, { forceMode: currentMode, sourceEntry: 'result_regenerate' }); }, 100);
  }, [currentMode, handleGenerate]);

  const handleRegenerateBatch = useCallback(async () => {
    setResult(null);
    setTaskStatus(null);
    const batchSize = availableGenerationSlots(activeCount, activeLimit);
    for (let i = 0; i < batchSize; i += 1) {
      await handleGenerate(undefined, {
        forceMode: currentMode,
        sourceEntry: 'result_regenerate',
      });
    }
  }, [activeCount, activeLimit, currentMode, handleGenerate]);

  const handleContinueEdit = useCallback(() => {
    if (!result?.imageUrl) return;
    setCurrentMode('edit');
    setReferenceImage(result.imageUrl);
    setReferenceImageUrl(result.imageUrl);
    setEditPrompt('');
    setResult(null);
    setTaskStatus(null);
  }, [result?.imageUrl]);

  const handleChangeStyleFromResult = useCallback(() => {
    setShowStyleModal(true);
  }, []);

  // ---- Recent usage ----
  const handleRecentSelect = useCallback((item: { prompt: string; style_id?: string | null; ratio?: string | null; model_id?: string; quality?: string }) => {
    setPrompt(item.prompt);
    if (item.style_id != null) setSelectedStyle(item.style_id);
    if (item.ratio != null) setSelectedRatio(item.ratio);
    if (item.model_id) {
      modelTouched.current = true;
      setSelectedModel(item.model_id);
    }
    if (item.quality) setSelectedQuality(item.quality);
  }, []);

  // ---- Derived display values ----
  const modelInfo = {
    name:
      selectedModelConfig?.name_zh ??
      selectedModelConfig?.name ??
      t('gen_modelShort'),
    icon: selectedModelConfig?.icon ?? modelIconMap[selectedModel] ?? 'ri-robot-2-line',
  };

  const outputItems = [
    {
      key: 'model',
      icon: modelInfo.icon,
      iconBg: '#6366F118',
      iconColor: '#6366F1',
      label: t('gen_modelFallback'),
      value: modelInfo.name,
      active: true,
      onClick: () => setShowModelSheet(true),
    },
    {
      key: 'ratio',
      icon: 'ri-aspect-ratio-line',
      iconBg: '#8B5CF618',
      iconColor: '#8B5CF6',
      label: t('gen_ratioFallback'),
      value: getSelectedRatioLabel() || t('gen_notSelected'),
      active: !!selectedRatio,
      onClick: () => setShowRatioSheet(true),
    },
    {
      key: 'quality',
      icon: 'ri-image-2-line',
      iconBg: '#F59E0B18',
      iconColor: '#F59E0B',
      label: t('gen_qualityFallback'),
      value: t(`gen_quality_${selectedQuality}_name`, { defaultValue: selectedQuality }),
      active: selectedQuality !== 'standard',
      onClick: () => setShowQualitySheet(true),
    },
  ];

  const renderInlineOutputChips = (options?: { showModel?: boolean; showRatio?: boolean }) => (
    <div className="flex items-center justify-end gap-1.5">
      {options?.showModel ? (
        <button
          onClick={() => setShowModelSheet(true)}
          disabled={isGenerating}
          className="flex items-center gap-1 rounded-full px-2.5 py-1 cursor-pointer active:scale-95 transition-all duration-200 disabled:opacity-50"
          style={{
            background: 'rgba(255,107,157,0.08)',
            border: '1px solid rgba(255,107,157,0.12)',
          }}
        >
          <i className={`${modelInfo.icon} text-[11px] text-gray-500`} />
          <span className="text-[11px] font-medium text-gray-600 whitespace-nowrap">
            {modelInfo.name}
          </span>
          <i className="ri-arrow-down-s-line text-gray-400 text-[10px]" />
        </button>
      ) : null}

      {options?.showRatio !== false ? (
        <button
          onClick={() => {
            if (entryConfig?.ratio_mode === 'selectable') setShowRatioSheet(true);
          }}
          disabled={isGenerating || entryConfig?.ratio_mode !== 'selectable'}
          className="flex items-center gap-1 rounded-full px-2.5 py-1 cursor-pointer active:scale-95 transition-all duration-200 disabled:opacity-50"
          style={{
            background: selectedRatio ? 'rgba(139,92,246,0.08)' : 'rgba(0,0,0,0.03)',
            border: selectedRatio ? '1px solid rgba(139,92,246,0.15)' : '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <i className="ri-aspect-ratio-line text-[11px] text-gray-500" />
          <span className="text-[11px] font-medium text-gray-600 whitespace-nowrap">
            {entryConfig?.ratio_mode === 'source'
              ? `${t('gen_followSource', '跟随原图')}${getSelectedRatioLabel() ? `（${getSelectedRatioLabel()}）` : ''}`
              : entryConfig?.ratio_mode === 'fixed'
                ? `${t('gen_fixedRatio', '固定比例')} ${entryConfig.fixed_ratio ?? ''}`
                : getSelectedRatioLabel() || t('gen_ratioFallback')}
          </span>
          <i className="ri-arrow-down-s-line text-gray-400 text-[10px]" />
        </button>
      ) : null}

      <button
        onClick={() => setShowQualitySheet(true)}
        disabled={isGenerating}
        className="flex items-center gap-1 rounded-full px-2.5 py-1 cursor-pointer active:scale-95 transition-all duration-200 disabled:opacity-50"
        style={{
          background: selectedQuality !== 'standard' ? 'rgba(245,158,11,0.08)' : 'rgba(0,0,0,0.03)',
          border: selectedQuality !== 'standard' ? '1px solid rgba(245,158,11,0.15)' : '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <i className="ri-image-2-line text-[11px] text-gray-500" />
        <span className="text-[11px] font-medium text-gray-600 whitespace-nowrap">
          {t(`gen_quality_${selectedQuality}_name`, { defaultValue: selectedQuality })}
        </span>
        <i className="ri-arrow-down-s-line text-gray-400 text-[10px]" />
      </button>
    </div>
  );

  // ---- Mode render functions ----

  const renderText2Img = () => (
    <>
      <div
        className="rounded-3xl p-3 mb-2"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 rounded-md flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
            <i className="ri-pencil-line text-white text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('gen_describeScene')}</span>
        </div>

        {referenceImage && (
          <div className="flex items-center gap-3 mb-2">
            <div className="relative w-12 h-12 rounded-xl overflow-hidden flex-shrink-0" style={{ border: '1px solid rgba(255,107,157,0.2)' }}>
              <img src={referenceImage} alt={t('reference_image_alt')} className="w-full h-full object-contain" />
              <button
                onClick={() => {
                  if (referencePreviewUrlRef.current) { URL.revokeObjectURL(referencePreviewUrlRef.current); referencePreviewUrlRef.current = null; }
                  setReferenceImage(null); setReferenceImageUrl(undefined);
                }}
                className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer"
              >
                <i className="ri-close-line text-[10px]"></i>
              </button>
            </div>
            <span className="text-xs text-gray-400">{t('gen_referenceUploaded')}</span>
          </div>
        )}

        {/* Simple textarea for text2img - ChatView handles the chat UI */}
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={isGenerating}
          placeholder={t('gen_placeholder')}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-300 resize-none outline-none leading-relaxed"
          rows={3}
          style={{ minHeight: '60px' }}
        />
      </div>

      <ConfigBar
        title={t('gen_creationSettings')}
        titleIcon="ri-paint-brush-line"
        titleIconColor="#FF6B9D"
        items={[
          {
            key: 'reference',
            icon: 'ri-image-add-line',
            iconBg: '#FF6B9D18',
            iconColor: '#FF6B9D',
            label: t('gen_referenceShort'),
            value: referenceImage ? t('gen_referenceUploaded') : t('gen_notAdded'),
            active: !!referenceImage,
            onClick: handleNativePhotoPick,
          },
          {
            key: 'style',
            icon: 'ri-palette-line',
            iconBg: '#A855F718',
            iconColor: '#A855F7',
            label: t('gen_styleShort'),
            value: getSelectedStyleName() || t('gen_notSelected'),
            active: !!selectedStyle,
            onClick: () => setShowStyleModal(true),
          },
          {
            key: 'template',
            icon: 'ri-layout-grid-line',
            iconBg: '#EC489918',
            iconColor: '#EC4899',
            label: t('gen_templateShort'),
            value: getSelectedTemplateName() || t('gen_notSelected'),
            active: !!selectedTemplate,
            onClick: () => setShowTemplateModal(true),
          },
        ]}
        disabled={isGenerating}
      />

      <div className="mb-2">
        <ConfigBar
          title={t('gen_outputSettings')}
          titleIcon="ri-settings-3-line"
          titleIconColor="#A855F7"
          items={outputItems}
          disabled={isGenerating}
        />
      </div>
    </>
  );

  const renderImg2Img = () => (
    <>
      <div className="rounded-[22px] p-3.5 mb-3.5" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,107,157,0.12)', boxShadow: '0 10px 28px rgba(17,24,39,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #F59E0B, #EF4444)' }}>
            <i className="ri-image-add-line text-white text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-gray-500 flex-1">{t('img2img_uploadReference')}</span>
          <span className="text-[10px] text-red-400">{t('gen_required')}</span>
        </div>
        {img2imgReferenceImages.length > 0 ? (
          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-xs font-medium text-gray-700">
                {t('img2img_referenceCount', { count: img2imgReferenceImages.length, max: IMG2IMG_REFERENCE_LIMIT })}
              </p>
              {img2imgReferenceImages.length < IMG2IMG_REFERENCE_LIMIT && (
                <button
                  type="button"
                  onClick={handleNativePhotoPick}
                  disabled={isGenerating}
                  className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-pink-500 cursor-pointer active:scale-95 transition-transform disabled:opacity-50"
                  style={{ background: 'rgba(255,107,157,0.08)', border: '1px solid rgba(255,107,157,0.12)' }}
                >
                  <i className="ri-add-line text-xs" />
                  <span>{t('img2img_addMore')}</span>
                </button>
              )}
            </div>
            <div className="flex gap-2.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {img2imgReferenceImages.map((image, index) => (
                <div
                  key={image.id}
                  className="relative w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 bg-gray-50"
                  style={{ border: '1px solid rgba(255,107,157,0.15)' }}
                >
                  <img src={image.previewUrl} alt={`reference ${index + 1}`} className="w-full h-full object-contain" />
                  {image.uploading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                      <div className="w-5 h-5 rounded-full border-2 border-pink-200 border-t-pink-500 animate-spin" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImg2ImgReferenceImage(image.id)}
                    disabled={isGenerating}
                    className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-close-line text-[10px]"></i>
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-1.5">{t('img2img_supportFormat')}</p>
          </div>
        ) : (
          <button onClick={handleNativePhotoPick} className="w-full h-16 rounded-2xl flex items-center justify-center gap-3 cursor-pointer active:scale-[0.98] transition-transform" style={{ background: 'rgba(255,107,157,0.05)', border: '2px dashed rgba(255,107,157,0.2)' }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,107,157,0.1)' }}>
              <i className="ri-upload-cloud-2-line text-xl text-pink-400"></i>
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600 font-medium">{t('img2img_clickUpload')}</p>
              <p className="text-[11px] text-gray-400">{t('img2img_supportFormat')}</p>
            </div>
          </button>
        )}
      </div>

      <div className="rounded-[22px] p-3.5 mb-3.5 min-h-[190px] flex flex-col" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,107,157,0.12)', boxShadow: '0 10px 28px rgba(17,24,39,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
            <i className="ri-pencil-line text-white text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('img2img_describeChange')}</span>
        </div>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={isGenerating}
          placeholder={t('img2img_placeholder')}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-300 resize-none outline-none leading-relaxed"
          rows={5}
          style={{ minHeight: '112px' }}
        />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <button onClick={() => setShowModelSheet(true)} className="flex items-center gap-1.5 rounded-full px-2.5 py-1 cursor-pointer active:scale-95 transition-all" style={{ background: 'rgba(255,107,157,0.08)', border: '1px solid rgba(255,107,157,0.12)' }}>
            <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
              <i className={`${modelInfo.icon} text-white text-[8px]`} />
            </div>
            <span className="text-[11px] font-medium text-gray-600 whitespace-nowrap">{modelInfo.name}</span>
            <i className="ri-arrow-down-s-line text-gray-400 text-[10px]" />
          </button>
          <div className="flex items-center gap-2">
            <PromptAIHelpButton
              profile={aiHelpProfile}
              disabled={!prompt.trim() || isGenerating || aiHelpDisabled}
              loading={inlineAIHelpLoading}
              onClick={() => { void handleInlineAIHelp(prompt, setPrompt); }}
            />
            <span className="text-xs text-gray-300">{prompt.length}</span>
          </div>
        </div>
        {inlineAIHelpError ? (
          <p className="mt-1 text-[11px] text-red-400" role="alert">{inlineAIHelpError}</p>
        ) : null}
      </div>

      <div className="-mt-2.5 mb-4 pr-1">
        {renderInlineOutputChips()}
      </div>
    </>
  );

  const renderEdit = () => (
    <>
      <div className="rounded-[22px] p-3.5 mb-3.5" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,107,157,0.12)', boxShadow: '0 10px 28px rgba(17,24,39,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #10B981, #06B6D4)' }}>
            <i className="ri-image-add-line text-white text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-gray-500 flex-1">{t('edit_uploadOriginal')}</span>
          <span className="text-[10px] text-red-400">{t('gen_required')}</span>
        </div>
        {referenceImage ? (
          <div className="flex items-center gap-3">
            <div className="relative w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0" style={{ border: '1px solid rgba(16,185,129,0.15)' }}>
              <img src={referenceImage} alt="original" className="w-full h-full object-contain" />
              <button onClick={() => { setReferenceImage(null); setReferenceImageUrl(undefined); }} className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer">
                <i className="ri-close-line text-[10px]"></i>
              </button>
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-700">{t('gen_referenceUploaded')}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{t('edit_tapToMarkArea')}</p>
            </div>
          </div>
        ) : (
          <button onClick={handleNativePhotoPick} className="w-full h-16 rounded-2xl flex items-center justify-center gap-3 cursor-pointer active:scale-[0.98] transition-transform" style={{ background: 'rgba(16,185,129,0.05)', border: '2px dashed rgba(16,185,129,0.2)' }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(16,185,129,0.1)' }}>
              <i className="ri-upload-cloud-2-line text-xl text-emerald-400"></i>
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600 font-medium">{t('edit_clickUpload')}</p>
              <p className="text-[11px] text-gray-400">{t('edit_supportFormat')}</p>
            </div>
          </button>
        )}
      </div>

      <div className="rounded-[22px] p-3.5 mb-3.5 min-h-[210px] flex flex-col" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,107,157,0.12)', boxShadow: '0 10px 28px rgba(17,24,39,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #10B981, #06B6D4)' }}>
            <i className="ri-magic-line text-white text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('edit_describeEdit')}</span>
        </div>
        <textarea
          value={editPrompt}
          onChange={(e) => setEditPrompt(e.target.value)}
          disabled={isGenerating}
          placeholder={t('edit_placeholder')}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          className="w-full bg-transparent text-sm text-gray-700 placeholder-gray-300 resize-none outline-none leading-relaxed"
          rows={6}
          style={{ minHeight: '132px' }}
        />
        <div className="flex items-center justify-between mt-1.5">
          <button onClick={() => setShowModelSheet(true)} className="flex items-center gap-1.5 rounded-full px-2.5 py-1 cursor-pointer active:scale-95 transition-all" style={{ background: 'rgba(255,107,157,0.08)', border: '1px solid rgba(255,107,157,0.12)' }}>
            <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
              <i className={`${modelInfo.icon} text-white text-[8px]`} />
            </div>
            <span className="text-[11px] font-medium text-gray-600 whitespace-nowrap">{modelInfo.name}</span>
            <i className="ri-arrow-down-s-line text-gray-400 text-[10px]" />
          </button>
          <div className="flex items-center gap-2">
            <PromptAIHelpButton
              profile={aiHelpProfile}
              disabled={!editPrompt.trim() || isGenerating || aiHelpDisabled}
              loading={inlineAIHelpLoading}
              onClick={() => { void handleInlineAIHelp(editPrompt, setEditPrompt); }}
            />
            <span className="text-xs text-gray-300">{editPrompt.length}</span>
          </div>
        </div>
        {inlineAIHelpError ? (
          <p className="mt-1 text-[11px] text-red-400" role="alert">{inlineAIHelpError}</p>
        ) : null}
      </div>

      <div className="-mt-2.5 mb-4 pr-1">
        {renderInlineOutputChips()}
      </div>
    </>
  );

  const renderRestore = () => (
    <>
      <div className="rounded-[22px] p-3.5 mb-3.5" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,107,157,0.12)', boxShadow: '0 10px 28px rgba(17,24,39,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}>
            <i className="ri-image-add-line text-white text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-gray-500 flex-1">{t('restore_uploadImage')}</span>
          <span className="text-[10px] text-red-400">{t('gen_required')}</span>
        </div>
        {referenceImage ? (
          <div className="flex items-center gap-3">
            <div className="relative w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0" style={{ border: '1px solid rgba(99,102,241,0.15)' }}>
              <img src={referenceImage} alt="restore" className="w-full h-full object-contain" />
              <button onClick={() => { setReferenceImage(null); setReferenceImageUrl(undefined); }} className="absolute top-0.5 right-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer">
                <i className="ri-close-line text-[10px]"></i>
              </button>
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-700">{t('gen_referenceUploaded')}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{t('restore_previewMode')}</p>
            </div>
          </div>
        ) : (
          <button onClick={handleNativePhotoPick} className="w-full h-16 rounded-2xl flex items-center justify-center gap-3 cursor-pointer active:scale-[0.98] transition-transform" style={{ background: 'rgba(99,102,241,0.05)', border: '2px dashed rgba(99,102,241,0.2)' }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(99,102,241,0.1)' }}>
              <i className="ri-upload-cloud-2-line text-xl text-indigo-400"></i>
            </div>
            <div className="text-left">
              <p className="text-sm text-gray-600 font-medium">{t('restore_clickUpload')}</p>
              <p className="text-[11px] text-gray-400">{t('restore_supportFormat')}</p>
            </div>
          </button>
        )}
      </div>

      <div className="rounded-[22px] p-3.5 mb-3.5" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,107,157,0.12)', boxShadow: '0 10px 28px rgba(17,24,39,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)' }}>
            <i className="ri-hd-line text-white text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-gray-500 flex-1">{t('restore_strength')}</span>
          <span className="text-xs font-semibold text-indigo-500">{restoreStrength}%</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-gray-400 whitespace-nowrap">{t('restore_gentle')}</span>
          <input
            type="range"
            min="0"
            max="100"
            value={restoreStrength}
            onChange={(e) => setRestoreStrength(Number(e.target.value))}
            className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer"
            style={{ background: `linear-gradient(to right, #6366F1 0%, #8B5CF6 ${restoreStrength}%, #E5E7EB ${restoreStrength}%, #E5E7EB 100%)` }}
          />
          <span className="text-[11px] text-gray-400 whitespace-nowrap">{t('restore_strong')}</span>
        </div>
      </div>

      <div className="-mt-2.5 mb-4 pr-1">
        {renderInlineOutputChips({ showModel: true })}
      </div>
    </>
  );

  const renderTryon = () => (
    <>
      <div className="flex gap-2.5 mb-3.5">
        {/* Person photo */}
        <div className="flex-1 min-w-0 rounded-[22px] p-3" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,107,157,0.12)', boxShadow: '0 10px 28px rgba(17,24,39,0.04)' }}>
          <div className="flex items-center gap-1.5 mb-2.5 min-w-0">
            <div className="w-4 h-4 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #EC4899, #F97316)' }}>
              <i className="ri-user-line text-white text-[10px]"></i>
            </div>
            <span className="text-[11px] font-semibold text-gray-500 flex-1 truncate">{t('tryon_uploadPerson')}</span>
            <span className="text-[9px] text-red-400 flex-shrink-0 ml-auto">{t('gen_required')}</span>
          </div>
          {referenceImage ? (
            <div className="relative w-full aspect-[3/3] rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,107,157,0.15)' }}>
              <img src={referenceImage} alt="person" className="w-full h-full object-contain" />
              {tryonModelUploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                  <div className="w-6 h-6 border-2 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
                </div>
              )}
              <button onClick={() => { tryonModelUploadSeq.current += 1; setTryonModelUploading(false); setReferenceImage(null); setReferenceImageUrl(undefined); setSelectedTryonModelId(null); }} className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer">
                <i className="ri-close-line text-[10px]"></i>
              </button>
            </div>
          ) : (
            <button onClick={handleNativePhotoPick} className="w-full aspect-[3/3] rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform" style={{ background: 'rgba(236,72,153,0.05)', border: '2px dashed rgba(236,72,153,0.2)' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(236,72,153,0.1)' }}>
                <i className="ri-user-add-line text-xl text-pink-400"></i>
              </div>
              <p className="text-[11px] text-gray-400 text-center px-1">{t('tryon_personTip')}</p>
            </button>
          )}
        </div>

        {/* Clothing photo */}
        <div className="flex-1 min-w-0 rounded-[22px] p-3" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,107,157,0.12)', boxShadow: '0 10px 28px rgba(17,24,39,0.04)' }}>
          <div className="flex items-center gap-1.5 mb-2.5 min-w-0">
            <div className="w-4 h-4 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #F97316, #EC4899)' }}>
              <i className="ri-t-shirt-line text-white text-[10px]"></i>
            </div>
            <span className="text-[11px] font-semibold text-gray-500 flex-1 truncate">{t('tryon_uploadClothing')}</span>
            <span className="text-[9px] text-red-400 flex-shrink-0 ml-auto">{t('gen_required')}</span>
          </div>
          {secondaryImage ? (
            <div className="relative w-full aspect-[3/3] rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,107,157,0.15)' }}>
              <img src={secondaryImage} alt="clothing" className="w-full h-full object-contain" />
              <button onClick={() => { setSecondaryImage(null); setSecondaryImageUrl(undefined); }} className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer">
                <i className="ri-close-line text-[10px]"></i>
              </button>
            </div>
          ) : (
            <button onClick={handleNativePhotoPickSecondary} className="w-full aspect-[3/3] rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform" style={{ background: 'rgba(249,115,22,0.05)', border: '2px dashed rgba(249,115,22,0.2)' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(249,115,22,0.1)' }}>
                <i className="ri-t-shirt-air-line text-xl text-orange-400"></i>
              </div>
              <p className="text-[11px] text-gray-400 text-center px-1">{t('tryon_clothingTip')}</p>
            </button>
          )}
        </div>
      </div>

      {!referenceImage && (
        <div className="rounded-[22px] p-3 mb-3.5" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,107,157,0.12)', boxShadow: '0 10px 28px rgba(17,24,39,0.04)' }}>
          <ModelGallery
            models={tryonModels}
            selectedModelId={selectedTryonModelId}
            onSelect={handleSelectTryonModel}
          />
        </div>
      )}

      <div className="rounded-[22px] p-3.5 mb-4" style={{ background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,107,157,0.12)', boxShadow: '0 10px 28px rgba(17,24,39,0.04)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #EC4899, #F97316)' }}>
            <i className="ri-body-scan-line text-white text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('tryon_selectPose')}</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[
            { id: 'natural', icon: 'ri-user-line', label: t('tryon_pose_natural') },
            { id: 'front', icon: 'ri-user-smile-line', label: t('tryon_pose_front') },
            { id: 'side', icon: 'ri-user-follow-line', label: t('tryon_pose_side') },
            { id: 'cross', icon: 'ri-user-star-line', label: t('tryon_pose_cross') },
          ].map((pose) => (
            <button
              key={pose.id}
              onClick={() => setSelectedPose(pose.id)}
              className={`flex flex-col items-center gap-1 py-2 rounded-xl cursor-pointer transition-all active:scale-95 ${selectedPose === pose.id ? 'bg-pink-50' : 'bg-gray-50'}`}
              style={selectedPose === pose.id ? { border: '1px solid rgba(255,107,157,0.2)' } : {}}
            >
              <i className={`${pose.icon} text-base ${selectedPose === pose.id ? 'text-pink-500' : 'text-gray-400'}`}></i>
              <span className={`text-[10px] ${selectedPose === pose.id ? 'text-pink-600 font-medium' : 'text-gray-500'}`}>{pose.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="-mt-2.5 mb-4 pr-1">
        {renderInlineOutputChips({ showModel: true })}
      </div>
    </>
  );

  // ---- Auth guards ----
  if (!authInitialized) {
    return (
      <div className="min-h-dvh font-nunito flex flex-col max-w-md mx-auto w-full" style={{ background: 'linear-gradient(160deg, #FFF5F9 0%, #F8F3FF 50%, #F3F6FF 100%)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-dvh font-nunito flex flex-col items-center justify-center px-5 relative overflow-hidden max-w-md mx-auto w-full" style={{ background: 'linear-gradient(160deg, #FFF5F9 0%, #F8F3FF 50%, #F3F6FF 100%)' }}>
        <div className="absolute top-0 right-0 w-56 h-56 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, #FF6B9D22 0%, transparent 70%)', transform: 'translate(20%, -20%)' }} />
        <div className="absolute bottom-20 left-0 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, #A855F718 0%, transparent 70%)', transform: 'translate(-30%, 0)' }} />
        <div className="w-24 h-24 rounded-3xl flex items-center justify-center mb-6" style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)' }}>
          <i className="ri-image-ai-line text-white text-4xl"></i>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">{t('gen_loginTitle')}</h2>
        <p className="text-sm text-gray-400 mb-8 text-center leading-relaxed whitespace-pre-line">{t('gen_loginDesc')}</p>
        <button
          onClick={() => navigate('/login?redirect=/generate')}
          className="w-full max-w-xs py-4 rounded-2xl text-white font-bold text-base cursor-pointer whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #FF6B9D 0%, #A855F7 100%)' }}
        >
          {t('gen_loginBtn')}
        </button>
      </div>
    );
  }

  // ---- Main render ----
  return (
    <div
      className="h-dvh max-h-dvh font-nunito flex flex-col relative overflow-hidden overscroll-none max-w-md mx-auto w-full"
      style={{ background: 'linear-gradient(160deg, #FFF5F9 0%, #F8F3FF 50%, #F3F6FF 100%)' }}
    >
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-56 h-56 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, #FF6B9D22 0%, transparent 70%)', transform: 'translate(20%, -20%)' }} />
      <div className="absolute top-32 left-0 w-40 h-40 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, #A855F718 0%, transparent 70%)', transform: 'translate(-30%, 0)' }} />
      <div className="absolute bottom-40 right-0 w-48 h-48 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, #FF6B9D15 0%, transparent 70%)', transform: 'translate(30%, 0)' }} />

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-4 pt-1.5 pb-3" style={{ paddingTop: 'calc(0.375rem + env(safe-area-inset-top))' }}>
        <button
          onClick={() => navigate('/')}
          data-testid="generation-back"
          aria-label="generation-back"
          className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-all active:scale-95"
          style={{ background: 'rgba(0,0,0,0.85)' }}
        >
          <i className="ri-arrow-left-line text-white text-sm"></i>
        </button>

        <h1
          className="pointer-events-none absolute left-1/2 -translate-x-1/2 -translate-y-1/2 text-base font-bold text-gray-800"
          style={{ top: 'calc(env(safe-area-inset-top) + 0.375rem + 1rem)' }}
        >
          {t('gen_title')}
        </h1>

        <div className="flex max-w-[46%] items-center justify-end">
          <button
            type="button"
            className="flex min-w-0 items-center gap-1 active:scale-95 transition-transform"
            onClick={() => navigate('/membership')}
          >
            <i className="ri-flashlight-fill text-amber-500 text-xs"></i>
            <span className="text-xs font-bold text-gray-700">{user.points}</span>
            <span className="hidden text-[10px] text-gray-400 min-[390px]:inline">
              {t('gen_pointsCostThisTime')}: {configuredPointsCost ?? '--'}
            </span>
          </button>
        </div>
      </div>

      {/* Mode Selector */}
      <div className="relative z-10 px-4 pb-0.5">
        <ModeSelector currentMode={currentMode} onModeChange={handleModeChange} />
      </div>

      {modeTasks.length > 0 && (
        <div className="relative z-10 flex gap-2 overflow-x-auto px-4 py-2" data-testid="generation-mode-task-strip">
          {modeTasks.map((task) => (
            <button
              key={task.gen_id}
              type="button"
              onClick={() => navigate('/profile?tab=history')}
              className="flex min-w-[150px] items-center gap-2 rounded-2xl bg-white/80 px-3 py-2 text-left text-[11px] shadow-sm"
            >
              <i className={['success', 'failed', 'blocked', 'timeout'].includes(task.status)
                ? (task.status === 'success' ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-error-warning-fill text-red-400')
                : 'ri-loader-4-line animate-spin text-purple-500'} />
              <span className="min-w-0 flex-1 truncate text-gray-600">
                {task.status === 'success'
                  ? t('gen_taskComplete', '生成完成')
                  : ['failed', 'blocked', 'timeout'].includes(task.status)
                    ? t('gen_taskFailed', '生成未完成')
                    : t('gen_generationContinuesInBackground', '后台生成中')}
              </span>
              <span className="text-gray-400">{task.quality}</span>
            </button>
          ))}
        </div>
      )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={currentMode === 'img2img' ? IMG2IMG_REFERENCE_ACCEPT : 'image/*'}
        multiple={currentMode === 'img2img'}
        onChange={(e) => { void handleFileChange(e, 'primary'); }}
        className="hidden"
      />
      <input
        ref={secondaryFileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => { void handleFileChange(e, 'secondary'); }}
        className="hidden"
      />

      {/* Main content */}
      <div
        className={`relative z-10 flex-1 overflow-hidden flex flex-col pt-2.5 ${currentMode === 'text2img' ? 'px-0' : 'px-4'}`}
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        {currentMode === 'text2img' ? (
          <ChatView
            onSend={handleChatSend}
            onRegenerate={handleRegenerate}
            isGenerating={isGenerating}
            activeGenerationId={activeGenerationId}
            result={result}
            config={{
              style: selectedStyle,
              ratio: selectedRatio,
              model: selectedModel,
              quality: selectedQuality,
              template: selectedTemplate,
            }}
            configLabels={{
              style: getSelectedStyleName(),
              ratio: getSelectedRatioLabel(),
              model: modelInfo.name,
              quality: t(`gen_quality_${selectedQuality}_name`, {
                defaultValue: selectedQuality,
              }),
              template: getSelectedTemplateName(),
            }}
            onOpenConfig={(type) => {
              if (type === 'style') setShowStyleModal(true);
              if (type === 'ratio') setShowRatioSheet(true);
              if (type === 'model') setShowModelSheet(true);
              if (type === 'quality') setShowQualitySheet(true);
              if (type === 'template') setShowTemplateModal(true);
            }}
            onAIHelp={handleAIHelp}
            aiHelpProfile={aiHelpProfile}
            aiHelpDisabled={aiHelpDisabled}
            user={user}
            pointsCost={configuredPointsCost}
            pointsBalance={user?.points ?? 0}
            imageEditPointsCost={resolveGenerationPointsCost()}
            onReferenceImageCountChange={(count) => {
              setChatReferenceImageCount(count);
              setChatCostOverride(count > 0 ? resolveGenerationPointsCost() : null);
            }}
            initialPrompt={prompt || undefined}
            submissionRejection={chatSubmissionRejection}
            onViewHistory={() => navigate('/profile?tab=history')}
          />
        ) : isGenerating ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-5">
            <div className="relative w-20 h-20 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full opacity-20 animate-ping" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', animationDuration: '1.5s' }} />
              <div className="absolute inset-2 rounded-full opacity-30" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }} />
              <div className="relative w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden bg-white" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                <img src={GENERATION_ACTION_LOGO_URL} alt="logo" className="w-12 h-12 object-contain rounded-xl animate-pulse" />
              </div>
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-pink-300/60 animate-spin" style={{ animationDuration: '6s' }} />
            </div>
            <div className="text-center">
              <p className="text-base font-bold text-gray-800">{t('gen_aiCreating')}</p>
              <p className="text-sm text-gray-400 mt-1">{t('gen_aiCreatingDesc')}</p>
            </div>
            <div className="w-52 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,107,157,0.15)' }}>
              <div className="h-full rounded-full animate-progress" style={{ background: 'linear-gradient(90deg, #FF6B9D, #A855F7)' }} />
            </div>
            <div className="px-4 py-2 rounded-full text-xs text-gray-500" style={{ background: 'rgba(255,255,255,0.7)' }}>
              {t('gen_highQuality')}
            </div>
          </div>
        ) : taskStatus && taskStatus !== 'success' ? (
          <TaskStatusArea
            status={taskStatus}
            reasonCode={taskReasonCode}
            onRetry={() => {
              setTaskStatus(null);
              void handleGenerate(undefined, { sourceEntry: 'result_regenerate' });
            }}
            onModifyPrompt={() => setTaskStatus(null)}
          />
        ) : result ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex items-center gap-2 mb-3 shrink-0">
              <div className="w-1.5 h-5 rounded-full" style={{ background: 'linear-gradient(180deg, #FF6B9D, #A855F7)' }} />
              <h2 className="text-sm font-bold text-gray-800">{t('gen_result')}</h2>
              <div className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium text-white" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)' }}>
                <i className="ri-check-line text-[10px]"></i>
                <span>{t('gen_success')}</span>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              <ResultCard
                imageUrl={result.imageUrl}
                thumbnailUrl={result.thumbnailUrl}
                styleName={result.styleName}
                ratio={result.ratio}
                prompt={result.prompt ?? ''}
                onRegenerate={handleRegenerate}
                onRegenerateBatch={handleRegenerateBatch}
                onContinueEdit={handleContinueEdit}
                onChangeStyle={handleChangeStyleFromResult}
                onShare={() => { track.shareOpen(); setShareSheet({ imageUrl: result!.imageUrl, prompt: result!.prompt ?? '', genId: result!.gen_id }); }}
                isVip={user?.isVip || false}
                genId={result.gen_id || ''}
                canDownloadHd={result.canDownloadHd ?? false}
                favorited={result.favorited ?? false}
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto min-h-0 pb-5 ios-scroll">
              {currentMode === 'img2img' && renderImg2Img()}
              {currentMode === 'edit' && renderEdit()}
              {currentMode === 'restore' && renderRestore()}
              {currentMode === 'tryon' && renderTryon()}
            </div>

            <div className="shrink-0 pt-3">
              <GenerateButton
                onClick={() => { void handleGenerate(); }}
                disabled={!canGenerate()}
                isGenerating={isGenerating}
                points={user?.points || 0}
                cost={displayedPointsCost ?? undefined}
              />
            </div>

            {user && (
              <div className="flex items-center justify-center gap-1.5 mt-1 shrink-0">
                <p className="text-[10px] text-gray-400">
                  {t('gen_pointsLeft')}{' '}
                  <span className="font-semibold text-gray-600">{user.points}</span>{' '}
                  {t('gen_points')} · {t('gen_pointsCost', { cost: displayedPointsCost ?? '--' })} · {t('gen_estimateTime')}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gen toast */}
      {genToast && (
        <div className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 px-4 py-2.5 rounded-2xl text-sm font-semibold text-white shadow-lg animate-slide-up" style={{ background: 'linear-gradient(135deg, #FF6B9D, #A855F7)', maxWidth: '80vw', textAlign: 'center' }}>
          {genToast}
        </div>
      )}

      {/* Modals */}
      <TemplateModal
        isOpen={showTemplateModal}
        onClose={() => { setShowTemplateModal(false); setTemplateCategory(undefined); setTemplateCategoryTitleKey(undefined); }}
        onSelect={handleTemplateSelect}
        category={templateCategory}
        categoryTitleKey={templateCategoryTitleKey}
      />
      <StyleModal
        isOpen={showStyleModal}
        selectedId={selectedStyle}
        onClose={() => setShowStyleModal(false)}
        onSelect={(id) => { pushEvent('generate_config_style', user?.userId, { styleId: id }); setSelectedStyle(id); }}
        isVip={user?.isVip || false}
        templateId={selectedTemplate ?? undefined}
      />
      <PointsModal isOpen={showPointsModal} onClose={() => setShowPointsModal(false)} cost={pointsModalCost} />
      <VipUpgradeSheet isOpen={showVipSheet} onClose={() => setShowVipSheet(false)} />

      {showInlineMembershipCta && !user?.isVip ? (
        <div
          className="fixed left-4 right-4 z-40 flex items-center gap-3 rounded-2xl bg-gray-900 p-3 text-white shadow-xl"
          style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        >
          <div className="flex-1">
            <p className="text-sm font-semibold">作品已完成，会员可同时生成 10 张</p>
            <p className="text-xs text-white/60">解锁高清下载与更多会员能力</p>
          </div>
          <button
            type="button"
            className="rounded-xl bg-gradient-to-r from-pink-500 to-purple-500 px-4 py-2 text-sm font-semibold"
            onClick={() => {
              if (inlineMembershipOffer) {
                void recordMembershipOfferEvent(
                  inlineMembershipOffer.offer.context_id,
                  'click',
                  { surface: 'inline' },
                );
              }
              sessionStorage.setItem('paySource', JSON.stringify({
                route: '/profile?tab=history',
                action: 'generation_success_offer',
                params: { gen_id: inlineMembershipOffer?.generationId },
                offer_context_id: inlineMembershipOffer?.offer.context_id,
              }));
              navigate('/membership');
            }}
          >
            查看优惠
          </button>
          <button
            type="button"
            aria-label="关闭会员提示"
            onClick={() => {
              if (inlineMembershipOffer) {
                void recordMembershipOfferEvent(
                  inlineMembershipOffer.offer.context_id,
                  'dismiss',
                  { surface: 'inline' },
                );
              }
              setShowInlineMembershipCta(false);
            }}
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {/* Model selector — uses v1's prop shape */}
      <ModelSelector
        value={selectedModel}
        onChange={(id) => {
          modelTouched.current = true;
          pushEvent('generate_config_model', user?.userId, { modelId: id });
          setSelectedModel(id);
        }}
        disabled={false}
        isOpen={showModelSheet}
        onClose={() => setShowModelSheet(false)}
        quality={selectedQuality}
        mode={selectionMode}
        referenceImageCount={selectionReferenceImageCount}
      />

      {/* Quality selector sheet */}
      {showQualitySheet && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowQualitySheet(false)} />
          <div className="relative w-full bg-white rounded-t-3xl px-5 pt-5 animate-slide-up" style={{ paddingBottom: 'calc(2.5rem + env(safe-area-inset-bottom))' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-800">{t('gen_qualityFallback')}</h3>
              <button onClick={() => setShowQualitySheet(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <QualitySelector value={selectedQuality} onChange={(v) => { setSelectedQuality(v); setShowQualitySheet(false); }} isVip={user?.isVip || false} disabled={false} modelId={selectedModel} supportedQualities={selectedModelConfig?.supported_qualities} />
          </div>
        </div>
      )}

      {/* Ratio selector */}
      <RatioSelector
        selectedRatio={selectedRatio}
        onSelect={(id) => { pushEvent('generate_config_ratio', user?.userId, { ratioId: id }); setSelectedRatio(id); }}
        externalOpen={showRatioSheet}
        onExternalClose={() => setShowRatioSheet(false)}
        allowedRatioIds={selectedModelConfig?.supported_ratios}
      />

      <RecentUsageModal
        isOpen={showRecentModal}
        onClose={() => setShowRecentModal(false)}
        onSelect={handleRecentSelect}
      />

      <ShareSheet
        isOpen={!!shareSheet}
        imageUrl={shareSheet?.imageUrl ?? ''}
        prompt={shareSheet?.prompt ?? ''}
        genId={shareSheet?.genId}
        onClose={() => setShareSheet(null)}
      />
    </div>
  );
}
